import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import {
  communityReviewInvalidations,
  communityReviewQueries,
} from "@circle-react/lib/queries/communityReviews";
import type {
  CommunityReviewPrompt,
  ReviewPromptSurface,
} from "@circle-react/types/CommunityReview";
import { useMutation, useQueryClient } from "react-query";

const hideSurface = (
  prompt: CommunityReviewPrompt,
  surface: ReviewPromptSurface,
): CommunityReviewPrompt => ({
  ...prompt,
  show_banner: surface === "banner" ? false : prompt.show_banner,
  show_review_prompt_modal:
    surface === "review_prompt_modal" ? false : prompt.show_review_prompt_modal,
  show_admin_nudge: surface === "admin_nudge" ? false : prompt.show_admin_nudge,
});

/**
 * Persists a banner / invitation modal dismissal on the server, so it holds
 * across devices. The surface hides right away and comes back if the request fails.
 */
export const useDismissReviewPrompt = () => {
  const queryClient = useQueryClient();
  const promptQueryKey = communityReviewQueries.prompt().queryKey;

  return useMutation({
    mutationFn: (surface: ReviewPromptSurface) =>
      reactQueryPost<void, { surface: ReviewPromptSurface }>(
        internalApi.communityReviews.dismissPrompt(),
        { surface },
      ),
    onMutate: async (surface: ReviewPromptSurface) => {
      await queryClient.cancelQueries({ queryKey: promptQueryKey });
      const previousPrompt =
        queryClient.getQueryData<CommunityReviewPrompt>(promptQueryKey);

      if (previousPrompt) {
        queryClient.setQueryData<CommunityReviewPrompt>(
          promptQueryKey,
          hideSurface(previousPrompt, surface),
        );
      }

      return { previousPrompt };
    },
    onError: (_error, _surface, context) => {
      if (context?.previousPrompt) {
        queryClient.setQueryData(promptQueryKey, context.previousPrompt);
      }
    },
    onSettled: () => {
      void communityReviewInvalidations.prompt();
    },
  });
};
